import { FC, MouseEvent, useEffect, useMemo, useState } from 'react'

import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'

import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'

import { RenderIf } from 'components/RenderIf'
import { CartProductImage } from './CartProductImage'
import { AgeProtection } from './AgeProtection'

import { ReactComponent as NoteIcon } from 'assets/notes/small_note.svg'
import { IconStroked } from 'components/IconsContainers/styled'

import {
  Discount,
  DiscountInline,
  IncludedItems,
  KitchenItem,
  KitchenItemContainer,
  MenuItem,
  Name,
  Note,
  Price,
  ProductStyled,
  SecondaryImage,
} from './styled'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { useOpenNote } from 'featureSliced/shared/hooks/openModals/useOpenNote'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { ICartProduct } from 'featureSliced/shared/store/generalTypes'

type CartProductProps = {
  product: ICartProduct
  isPriceFromStorage?: boolean
  altCount?: number
  isSelected?: boolean
  handleProductClick?: (
    productId: number,
    e: MouseEvent<HTMLElement>,
    product: ICartProduct
  ) => void
}

export const CartProduct: FC<CartProductProps> = ({
  product,
  isPriceFromStorage = false,
  altCount,
  isSelected,
  handleProductClick,
}) => {
  // Hooks
  const openNote = useOpenNote()
  const { getProductName } = useGetTranslatedNames()
  const { getProductPrice, getMenuItemPrice } = useGetProductPrice()

  // Store
  const productsList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const currency = useMySelector((state) => state.app.companyData).Currency
  const paymentVariant = useMySelector((state) => state.app.menu).paymentVariant
  const notesAvailable = useMySelector((state) => state.app.menu).notesAvailable

  const productFull: any = useMemo(() => {
    const findById = (items: IProduct[], id: number): IProduct | undefined => {
      for (const item of items) {
        if (item.id === id) return item
        if (item.innerCategoryItems?.length) {
          const found = findById(item.innerCategoryItems as IProduct[], id)
          if (found) return found
        }
      }
      return undefined
    }
    const found = findById(productsList || [], product?.productId)
    return found ? { ...product, ...found } : product
  }, [productsList, product])

  // State
  const [productsNotes, setProductsNotes] = useState<{
    [key: string]: string
  }>({})

  // useEffects
  useEffect(() => {
    setProductsNotes(handleLocalStorage('products_notes'))
  }, [])

  // Functions
  const handleNoteClick = (e: any) => {
    if (notesAvailable === 1) {
      e.stopPropagation()
      openNote(
        product?.productId,
        product?.categoryId,
        setProductsNotes,
        !(location.pathname === '/payment' && paymentVariant === 2)
      )
    }
  }

  const getMenuItems = () => {
    if (typeof product?.menuItems === 'string') {
      return JSON.parse(product?.menuItems || '[]')
    }
    if (Array.isArray(product?.menuItems)) {
      return product?.menuItems
    }
  }

  const getIsMenuItemsShown = () => {
    if (typeof product?.menuItems === 'string') {
      return Boolean(getMenuItems()?.length)
    }
    if (Array.isArray(product?.menuItems)) {
      return Boolean(getMenuItems()?.length)
    }
    return false
  }

  // TODO: duplicates. move to helpers
  const getIncludedItemPrice = (price = 0, count = 0) => {
    if (price && count) {
      return `${count} x ${convertToPrice(price)} ${currency || ''}`
    }
    if (!price && count) {
      return `x ${count}`
    }
    if (price && !count) {
      return `${convertToPrice(price)} ${currency || ''}`
    }
    if (!price && !count) {
      return ''
    }
    return ''
  }

  const kitchenInfoParsed = JSON.parse(product?.kitchenInfo || '{}')
  const kitchenFreeInfoParsed = JSON.parse(product?.kitchenFreeInfo || '{}')

  const shouldRenderKI = Object.values(kitchenInfoParsed).some(Boolean)
  const shouldRenderKFI = Object.values(kitchenFreeInfoParsed).some(Boolean)

  const { finalPrice, finalPriceString, priceOG, discountValue } =
    getProductPrice(
      productFull as IProduct,
      productFull?.categoryId,
      product?.menuItems?.length
    )

  return (
    <ProductStyled
      id={`product_${product?.productId}`}
      isSelected={isSelected}
      // TODO: add type
      onClick={(e: any) => handleProductClick?.(product?.productId, e, product)}
    >
      <CartProductImage product={productFull} />
      <Name altCount={altCount}>
        {/* <p>{productFull?.title}</p> */}
        <p>{getProductName(productFull)}</p>
      </Name>
      <Note>
        <RenderIf
          condition={
            !!productsNotes?.[
              `${product?.categoryId || 0}_${product?.productId || 0}`
            ]
          }
        >
          <p>
            {
              productsNotes?.[
                `${product?.categoryId || 0}_${product?.productId || 0}`
              ]
            }
          </p>
        </RenderIf>
      </Note>

      <AgeProtection product={productFull} />

      {/* Notes */}
      <RenderIf
        condition={
          notesAvailable === 1 &&
          !(location.pathname === '/payment' && paymentVariant === 2)
        }
      >
        <SecondaryImage onClick={handleNoteClick}>
          <IconStroked>
            <NoteIcon />
          </IconStroked>
        </SecondaryImage>
      </RenderIf>

      {/* Discount */}
      <RenderIf condition={Boolean(discountValue)}>
        <Discount>
          <span>{product.count}</span>
          &thinsp;
          <span>x</span>
          &thinsp;
          <span>{convertToPrice(priceOG)}</span>
          &thinsp;
          <span>{currency || ''}</span>
        </Discount>
      </RenderIf>

      {/* Price */}
      <RenderIf condition={Boolean(finalPrice)}>
        <Price>
          <span>{product.count}</span>
          &thinsp;
          <span>x</span>
          &thinsp;
          <span>
            {isPriceFromStorage
              ? convertToPrice(product?.price)
              : finalPriceString}
          </span>
          &thinsp;
          <span>{currency || ''}</span>
        </Price>
      </RenderIf>

      {/* NO Price */}
      {/* NOTE: deprecated */}
      <RenderIf condition={Boolean(!finalPrice)}>
        <Price>
          <span>x {product.count}</span>
        </Price>
      </RenderIf>

      {/* Additional info */}
      <RenderIf
        condition={
          !!product?.menuItems?.length || shouldRenderKI || shouldRenderKFI
        }
      >
        <IncludedItems>
          <RenderIf condition={shouldRenderKI}>
            <KitchenItemContainer>
              {Object.values(kitchenInfoParsed)?.map((el) => (
                <KitchenItem key={`kitchenInfo_${el}`}>
                  {el as string}
                </KitchenItem>
              ))}
            </KitchenItemContainer>
          </RenderIf>

          <RenderIf condition={shouldRenderKFI}>
            <KitchenItemContainer>
              {Object.values(kitchenFreeInfoParsed)?.map((el) => (
                <RenderIf key={`kitchenFreeInfo_${el}`} condition={Boolean(el)}>
                  <KitchenItem>{el as string}</KitchenItem>
                </RenderIf>
              ))}
            </KitchenItemContainer>
          </RenderIf>

          <RenderIf condition={getIsMenuItemsShown()}>
            {getMenuItems()?.map((currentItem: any) => {
              const itemPrice = getMenuItemPrice(currentItem)
              return (
                <MenuItem
                  key={`includedItem2_${currentItem.productId}_${currentItem.recordGuid}`}
                >
                  {`${currentItem.name} (${getIncludedItemPrice(
                    itemPrice.finalPrice,
                    product?.count
                  )})`}
                  <RenderIf
                    condition={currentItem.isApplyDiscount && product.discountP}
                  >
                    <DiscountInline>-{product.discountP}%</DiscountInline>
                  </RenderIf>
                </MenuItem>
              )
            })}
          </RenderIf>
        </IncludedItems>
      </RenderIf>
    </ProductStyled>
  )
}
